/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { Outlet, useNavigation, ScrollRestoration } from 'react-router';

import { SkipNavContent, Spinner } from 'sarsaparilla';

import { Footer } from 'site-kit';

import { ExternalPageFrame } from './ExternalPageFrame';
import { ScrollToHash } from './ScrollToHash';
import { TopNavigationExternal } from '../TopNavigationExternal';
import { LoadingProgress } from './LoadingProgress';

interface ExternalAppLayoutProps {
    className?: string;
    /** Controls guest sign for auth modals. This is also gated by GUEST_LOGIN_ALLOWED_PATH which controls paths guests are allowed to access. */
    isGuestAllowed?: boolean;
    /** Controls the loading UI to use (requires react router route loaders) */
    globalLoadingUI?: 'spinner' | 'progressBar';
    /** Fallback to use when the route is suspended. Defaults to a spinner. */
    suspenseFallback?: React.ReactNode;
    /**
     * When true (default), wraps the route outlet in a plain `<main>`.
     * Set to false when the app needs a custom landmark structure (e.g. sidebar
     * `<nav>` + `<main>`) and will provide `<main>` in the outlet itself.
     */
    wrapOutletInMain?: boolean;
}

/**
 * Shared layout for external apps: nav, loading UI, route outlet, and footer.
 * Uses `Outlet`, `useNavigation`, and `ScrollRestoration`, so
 * it is only for apps that use React Router's data mode (`createBrowserRouter`).
 *
 * Mount as a route `element` under a data router; wrap the app with
 * `ExternalAppProviders` for LaunchDarkly, React Query, Recaptcha, and Auth.
 */
export function ExternalAppLayout({
    className,
    isGuestAllowed,
    globalLoadingUI,
    suspenseFallback,
    wrapOutletInMain = true,
}: ExternalAppLayoutProps) {
    const navigation = useNavigation();

    const outlet = (
        <React.Suspense fallback={suspenseFallback ?? <Spinner />}>
            {globalLoadingUI === 'spinner' && navigation.state === 'loading' ? (
                <Spinner />
            ) : (
                <Outlet />
            )}
        </React.Suspense>
    );

    return (
        <>
            <ScrollRestoration
                getKey={(locationObject) => {
                    return locationObject.pathname;
                }}
            />
            <ScrollToHash />

            <ExternalPageFrame className={className}>
                <TopNavigationExternal isGuestAllowed={isGuestAllowed} />

                {globalLoadingUI === 'progressBar' ? <LoadingProgress /> : null}

                <SkipNavContent>
                    {wrapOutletInMain ? <main>{outlet}</main> : outlet}
                </SkipNavContent>
                <Footer />
            </ExternalPageFrame>
        </>
    );
}
